import { z } from 'zod';
import { accessoryVariantLabel, assessedAccessoryTypeId, floorAccessoryContext, floorAccessoryDefault, floorAccessoryFamilies } from './accessory-choices.js';
import { newSelection } from './base-pricing.js';
export const TrimSpecificationRequest = z.object({
    floor_product_id: z.string().trim().min(1).max(160),
    floor_variant_id: z.string().trim().min(1).max(160).nullable(),
    kind: z.enum(['trims', 'scotia']),
    profile: z.string().trim().min(1).max(160),
    product_id: z.string().trim().min(1).max(160).optional(),
    variant_id: z.string().trim().min(1).max(160).nullable().optional(),
});
export class TrimSpecificationError extends Error {
}
/** Server-owned eligibility and identity only: no price, pack optimisation or measured-quantity changes. */
export function previewTrimSpecification(input, products, catalogue) {
    const floors = products.filter(product => product.id === input.floor_product_id);
    if (floors.length !== 1)
        throw new TrimSpecificationError('The floor product is unavailable or ambiguous.');
    const floor = floors[0];
    if (new Set(floor.variants.map(variant => variant.id)).size !== floor.variants.length)
        throw new TrimSpecificationError('The floor catalogue contains ambiguous identities.');
    if (input.floor_variant_id && !floor.variants.some(variant => variant.id === input.floor_variant_id))
        throw new TrimSpecificationError('The colour does not belong to the selected floor.');
    const selection = { ...newSelection('trim-preview', floor.id), variantId: input.floor_variant_id ?? undefined };
    const context = floorAccessoryContext(floor, selection);
    const types = floorAccessoryFamilies(catalogue, context.carpet, context.manufacturerId).find(family => family.kind === input.kind)?.types ?? [];
    if (!types.length)
        throw new TrimSpecificationError('No eligible specification is available for this floor.');
    if (new Set(types.map(type => type.product_id)).size !== types.length || types.some(type => new Set(type.variants.map(variant => variant.variant_id)).size !== type.variants.length))
        throw new TrimSpecificationError('The accessory catalogue contains ambiguous identities.');
    if (input.variant_id !== undefined && !input.product_id)
        throw new TrimSpecificationError('Choose a product before its specification.');
    const profileProductId = assessedAccessoryTypeId(types, [input.profile]);
    const genericProfile = /^(trim|trims|scotia)$/i.test(input.profile.trim());
    if (input.product_id && !genericProfile && profileProductId !== input.product_id)
        throw new TrimSpecificationError('This product does not match the measured trim profile.');
    let patch = null;
    if (input.product_id) {
        const type = types.find(candidate => candidate.product_id === input.product_id);
        if (!type)
            throw new TrimSpecificationError('This product is not eligible for the selected floor.');
        if (input.variant_id === undefined) {
            const variant = floorAccessoryDefault(input.kind, type, context.boardThickness);
            patch = { productID: type.product_id, variantID: variant?.variant_id ?? null, specificationStatus: variant ? 'proposed' : 'unresolved', stockLengthMM: variant?.length_mm ?? null };
        }
        else if (input.variant_id === null) {
            patch = { productID: type.product_id, variantID: null, specificationStatus: 'unresolved', stockLengthMM: null };
        }
        else {
            const variant = type.variants.find(candidate => candidate.variant_id === input.variant_id);
            if (!variant)
                throw new TrimSpecificationError('This specification is not eligible for the selected floor.');
            patch = { productID: type.product_id, variantID: variant.variant_id, specificationStatus: 'confirmed', stockLengthMM: variant.length_mm };
        }
    }
    return { types: types.map(type => ({ product_id: type.product_id, name: type.name, unit: type.unit,
            default_variant_id: floorAccessoryDefault(input.kind, type, context.boardThickness)?.variant_id ?? null,
            variants: type.variants.map(variant => ({ variant_id: variant.variant_id, display_label: accessoryVariantLabel(variant), stock_length_mm: variant.length_mm })) })), patch };
}
//# sourceMappingURL=trim-specification.js.map